/**
 * ReviewExtractedDataModal.js - Modal para revisar y editar datos extraídos por IA
 * Muestra formulario prellenado con badges de confianza y validación visual
 */
import React, { useState, useEffect } from 'react';

const TIPOS_CONSULTA = [
  'Consulta general',
  'Vacunación',
  'Desparasitación',
  'Emergencia',
  'Cirugía',
  'Revisión',
  'Control',
  'Otro'
];

// Frecuencias expandidas incluyendo no estándar
const FRECUENCIAS = [
  { value: 4, label: 'Cada 4 horas (6 veces/día)' },
  { value: 6, label: 'Cada 6 horas (4 veces/día)' },
  { value: 8, label: 'Cada 8 horas (3 veces/día)' },
  { value: 12, label: 'Cada 12 horas (2 veces/día)' },
  { value: 24, label: 'Cada 24 horas (1 vez/día)' },
  { value: 48, label: 'Cada 48 horas (cada 2 días)' },
  { value: 0, label: 'Otra frecuencia...' }
];

export default function ReviewExtractedDataModal({
  show,
  onClose,
  onSave,
  onRescan,
  extractedData,
  loading
}) {
  const [formData, setFormData] = useState({
    tipo: '',
    fecha: new Date().toISOString().slice(0, 16), // Incluir fecha y hora (formato datetime-local)
    temperatura: '',
    peso: '',
    frecuenciaCardiaca: '',
    notas: '',
    medicamentos: [],
    // Nuevos campos de resumen inteligente
    titulo: null,
    resumen: null,
    clinica: null,
    veterinario: null,
    fechaConsulta: null,
    diagnostico: null
  });

  // Inicializar formulario con datos extraídos
  useEffect(() => {
    if (extractedData) {
      // Usar fechaConsulta de la IA si está disponible, sino fecha actual
      let fechaInicial = new Date().toISOString().slice(0, 16);
      if (extractedData.fechaConsulta) {
        // fechaConsulta viene como YYYY-MM-DD, agregar hora actual
        const now = new Date();
        const hora = now.toTimeString().slice(0, 5);
        fechaInicial = `${extractedData.fechaConsulta}T${hora}`;
      }

      setFormData({
        tipo: extractedData.tipoConsulta || 'Consulta general',
        fecha: fechaInicial,
        temperatura: extractedData.temperatura || '',
        peso: extractedData.peso || '',
        frecuenciaCardiaca: extractedData.frecuenciaCardiaca || '',
        notas: [
          extractedData.diagnostico ? `Diagnóstico: ${extractedData.diagnostico}` : '',
          extractedData.observaciones || ''
        ].filter(Boolean).join('\n\n'),
        medicamentos: (extractedData.medicamentos || []).map(med => ({
          ...med,
          frecuenciaPersonalizada: !FRECUENCIAS.some(f => f.value === med.frecuenciaHoras && f.value !== 0)
        })),
        // Nuevos campos de resumen inteligente (auto-generados, no editables)
        titulo: extractedData.titulo || null,
        resumen: extractedData.resumen || null,
        clinica: extractedData.clinica || null,
        veterinario: extractedData.veterinario || null,
        fechaConsulta: extractedData.fechaConsulta || null,
        diagnostico: extractedData.diagnostico || null
      });
    }
  }, [extractedData]);

  if (!show) return null;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleMedicamentoChange = (index, field, value) => {
    setFormData(prev => {
      const meds = [...prev.medicamentos];
      if (field === 'frecuenciaHoras' && value === 0) {
        // Cambiar a frecuencia personalizada
        meds[index] = { ...meds[index], frecuenciaHoras: '', frecuenciaPersonalizada: true };
      } else if (field === 'frecuenciaHoras') {
        meds[index] = { ...meds[index], frecuenciaHoras: value, frecuenciaPersonalizada: false };
      } else {
        meds[index] = { ...meds[index], [field]: value };
      }
      return { ...prev, medicamentos: meds };
    });
  };

  const handleRemoveMedicamento = (index) => {
    setFormData(prev => ({
      ...prev,
      medicamentos: prev.medicamentos.filter((_, i) => i !== index)
    }));
  };

  const handleAddMedicamento = () => {
    setFormData(prev => ({
      ...prev,
      medicamentos: [
        ...prev.medicamentos,
        { nombre: '', dosis: '', frecuenciaHoras: 12, duracionDias: null, instrucciones: '', confianza: 1, frecuenciaPersonalizada: false }
      ]
    }));
  };

  const handleSubmit = () => {
    onSave(formData);
  };

  const getConfidenceBadge = (confidence) => {
    if (confidence >= 0.9) {
      return <span style={{ color: '#4CAF50', fontSize: '12px', marginLeft: '8px' }}>✅ Alta</span>;
    } else if (confidence >= 0.7) {
      return <span style={{ color: '#ff9800', fontSize: '12px', marginLeft: '8px' }}>⚠️ Media</span>;
    } else {
      return <span style={{ color: '#f44336', fontSize: '12px', marginLeft: '8px' }}>⚠️ Revisar</span>;
    }
  };

  // Verificar campos faltantes en medicamentos
  const getMedicamentoWarnings = (med) => {
    const warnings = [];
    if (!med.nombre) warnings.push('Sin nombre');
    if (!med.dosis) warnings.push('Sin dosis');
    if (!med.duracionDias) warnings.push('Sin duración');
    return warnings;
  };

  // Contar medicamentos con advertencias
  const medicamentosConAdvertencias = formData.medicamentos.filter(
    med => !med.nombre || !med.dosis || !med.duracionDias
  ).length;

  const overallConfidence = extractedData?.extractionConfidence || 0;

  return (
    <div className="modal-overlay review-data-overlay" onClick={onClose}>
      <div
        className="modal-content review-data-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3>Revisar datos extraídos</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body" style={{ padding: '24px' }}>
          {/* Banner de confianza */}
          <div style={{
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '20px',
            backgroundColor: overallConfidence >= 0.9 ? '#e8f5e9' :
                            overallConfidence >= 0.7 ? '#fff8e1' : '#ffebee',
            border: `1px solid ${overallConfidence >= 0.9 ? '#4CAF50' :
                                 overallConfidence >= 0.7 ? '#ff9800' : '#f44336'}`
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{overallConfidence >= 0.9 ? '✅' : '⚠️'}</span>
              <span style={{ fontWeight: '500' }}>
                {overallConfidence >= 0.9 ? 'Análisis completado con alta confianza' :
                 overallConfidence >= 0.7 ? 'Análisis completado. Revisa los datos antes de guardar.' :
                 'Texto manuscrito o de baja calidad detectado. Revisa CUIDADOSAMENTE todos los datos.'}
              </span>
            </div>
            {extractedData?.isHandwritten && (
              <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#666' }}>
                Se detectó texto manuscrito. Verifica especialmente nombres de medicamentos y dosis.
              </p>
            )}
          </div>

          {/* Idioma detectado */}
          {extractedData?.idioma && extractedData.idioma !== 'español' && (
            <div style={{
              display: 'flex',
              gap: '8px',
              marginBottom: '16px',
              fontSize: '13px',
              flexWrap: 'wrap'
            }}>
              <span style={{
                backgroundColor: '#e3f2fd',
                padding: '4px 8px',
                borderRadius: '4px',
                color: '#1565c0'
              }}>
                🌍 Idioma original: {extractedData.idioma}
              </span>
              <span style={{
                backgroundColor: '#f3e5f5',
                padding: '4px 8px',
                borderRadius: '4px',
                color: '#7b1fa2'
              }}>
                ✨ Traducido automáticamente
              </span>
              <span style={{
                backgroundColor: '#fff3e0',
                padding: '4px 8px',
                borderRadius: '4px',
                color: '#e65100'
              }}>
                ⚠️ Revisa la traducción
              </span>
            </div>
          )}

          {/* Warnings */}
          {extractedData?.warnings?.length > 0 && (
            <div style={{
              backgroundColor: '#fff3e0',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '13px'
            }}>
              {extractedData.warnings.map((w, i) => (
                <p key={i} style={{ margin: i === 0 ? 0 : '8px 0 0' }}>⚠️ {w}</p>
              ))}
            </div>
          )}

          {/* Datos de clínica extraídos */}
          {(extractedData?.clinica || extractedData?.veterinario || extractedData?.fechaConsulta) && (
            <div style={{
              backgroundColor: '#f3e8ff',
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '13px',
              border: '1px solid #d8b4fe'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '8px', color: '#7c3aed' }}>
                🏥 Datos de la consulta extraídos:
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                {extractedData.clinica && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ color: '#7c3aed' }}>🏨</span>
                    <span>{extractedData.clinica}</span>
                  </span>
                )}
                {extractedData.veterinario && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ color: '#7c3aed' }}>👨‍⚕️</span>
                    <span>{extractedData.veterinario}</span>
                  </span>
                )}
                {extractedData.fechaConsulta && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ color: '#7c3aed' }}>📅</span>
                    <span>{new Date(extractedData.fechaConsulta + 'T00:00:00').toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Título y resumen generados */}
          {extractedData?.titulo && (
            <div style={{
              backgroundColor: '#f0fdf4',
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '13px',
              border: '1px solid #bbf7d0'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '4px', color: '#16a34a' }}>
                📝 Título generado: <span style={{ fontWeight: '500', color: '#333' }}>{extractedData.titulo}</span>
              </div>
              {extractedData.resumen && (
                <div style={{ color: '#666', marginTop: '4px', fontSize: '12px' }}>
                  {extractedData.resumen}
                </div>
              )}
            </div>
          )}

          {/* Indicador de receta adjunta */}
          <div style={{
            backgroundColor: '#e8f5e9',
            padding: '10px 14px',
            borderRadius: '6px',
            marginBottom: '20px',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span>📎</span>
            <span>La receta original se adjuntará automáticamente a esta entrada</span>
          </div>

          {/* Formulario */}
          <div style={{ display: 'grid', gap: '16px' }}>
            {/* Tipo y Fecha */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                  Tipo de consulta
                  <span style={{ color: '#4CAF50', fontSize: '11px', marginLeft: '8px' }}>✨ IA</span>
                </label>
                <select
                  value={formData.tipo}
                  onChange={(e) => handleChange('tipo', e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}
                >
                  {TIPOS_CONSULTA.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Fecha y hora</label>
                <input
                  type="datetime-local"
                  value={formData.fecha}
                  onChange={(e) => handleChange('fecha', e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
            </div>

            {/* Signos vitales */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                  Temperatura (°C)
                  {formData.temperatura && <span style={{ color: '#4CAF50', fontSize: '11px', marginLeft: '8px' }}>✨ IA</span>}
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.temperatura}
                  onChange={(e) => handleChange('temperatura', e.target.value)}
                  placeholder="38.5"
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                  Peso (kg)
                  {formData.peso && <span style={{ color: '#4CAF50', fontSize: '11px', marginLeft: '8px' }}>✨ IA</span>}
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.peso}
                  onChange={(e) => handleChange('peso', e.target.value)}
                  placeholder="10.5"
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                  Frec. cardíaca
                  {formData.frecuenciaCardiaca && <span style={{ color: '#4CAF50', fontSize: '11px', marginLeft: '8px' }}>✨ IA</span>}
                </label>
                <input
                  type="number"
                  value={formData.frecuenciaCardiaca}
                  onChange={(e) => handleChange('frecuenciaCardiaca', e.target.value)}
                  placeholder="80 lpm"
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
            </div>

            {/* Notas/Observaciones */}
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                Diagnóstico y observaciones
                <span style={{ color: '#4CAF50', fontSize: '11px', marginLeft: '8px' }}>✨ IA</span>
              </label>
              <textarea
                value={formData.notas}
                onChange={(e) => handleChange('notas', e.target.value)}
                rows={4}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '6px',
                  border: '1px solid #ddd',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Medicamentos */}
            <div style={{ borderTop: '1px solid #eee', paddingTop: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div>
                  <h4 style={{ margin: 0 }}>
                    💊 Medicamentos extraídos ({formData.medicamentos.length})
                  </h4>
                  {medicamentosConAdvertencias > 0 && (
                    <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#f44336' }}>
                      ⚠️ {medicamentosConAdvertencias} medicamento(s) con datos incompletos
                    </p>
                  )}
                </div>
                <button
                  onClick={handleAddMedicamento}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #4CAF50',
                    borderRadius: '4px',
                    backgroundColor: '#fff',
                    color: '#4CAF50',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  + Agregar medicamento
                </button>
              </div>

              {formData.medicamentos.length === 0 ? (
                <p style={{ color: '#888', fontSize: '14px', textAlign: 'center', padding: '20px' }}>
                  No se detectaron medicamentos en la receta
                </p>
              ) : (
                formData.medicamentos.map((med, idx) => {
                  const warnings = getMedicamentoWarnings(med);
                  const hasWarnings = warnings.length > 0;

                  return (
                    <div
                      key={idx}
                      style={{
                        backgroundColor: hasWarnings ? '#fff8e1' : '#f9f9f9',
                        borderRadius: '8px',
                        padding: '16px',
                        marginBottom: '12px',
                        border: hasWarnings ? '2px solid #ff9800' :
                               med.confianza < 0.7 ? '1px solid #ff9800' : '1px solid #e0e0e0'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <div>
                          <span style={{ fontWeight: '500' }}>
                            {idx + 1}️⃣ {med.nombre || 'Medicamento sin nombre'}
                            {med.confianza !== undefined && getConfidenceBadge(med.confianza)}
                            <span style={{ color: '#4CAF50', fontSize: '11px', marginLeft: '8px' }}>✨ IA</span>
                          </span>
                          {hasWarnings && (
                            <div style={{ fontSize: '11px', color: '#f57c00', marginTop: '4px' }}>
                              ⚠️ Revisar: {warnings.join(', ')}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => handleRemoveMedicamento(idx)}
                          style={{
                            padding: '4px 8px',
                            border: 'none',
                            backgroundColor: '#ffebee',
                            color: '#c62828',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Eliminar
                        </button>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div>
                          <label style={{ fontSize: '12px', color: !med.nombre ? '#f44336' : '#666' }}>
                            Nombre {!med.nombre && '*'}
                          </label>
                          <input
                            type="text"
                            value={med.nombre || ''}
                            onChange={(e) => handleMedicamentoChange(idx, 'nombre', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '8px',
                              borderRadius: '4px',
                              border: !med.nombre ? '2px solid #f44336' : '1px solid #ddd',
                              backgroundColor: !med.nombre ? '#ffebee' : '#fff'
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: '12px', color: !med.dosis ? '#ff9800' : '#666' }}>
                            Dosis {!med.dosis && '*'}
                          </label>
                          <input
                            type="text"
                            value={med.dosis || ''}
                            onChange={(e) => handleMedicamentoChange(idx, 'dosis', e.target.value)}
                            placeholder="10mg, 5ml..."
                            style={{
                              width: '100%',
                              padding: '8px',
                              borderRadius: '4px',
                              border: !med.dosis ? '2px solid #ff9800' : '1px solid #ddd',
                              backgroundColor: !med.dosis ? '#fff8e1' : '#fff'
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: '12px', color: '#666' }}>Frecuencia</label>
                          {med.frecuenciaPersonalizada ? (
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <input
                                type="number"
                                value={med.frecuenciaHoras || ''}
                                onChange={(e) => handleMedicamentoChange(idx, 'frecuenciaHoras', parseInt(e.target.value) || '')}
                                placeholder="Horas"
                                style={{ width: '80px', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                              />
                              <span style={{ alignSelf: 'center', fontSize: '12px', color: '#666' }}>horas</span>
                              <button
                                onClick={() => handleMedicamentoChange(idx, 'frecuenciaHoras', 12)}
                                style={{
                                  padding: '4px 8px',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px',
                                  backgroundColor: '#fff',
                                  cursor: 'pointer',
                                  fontSize: '11px'
                                }}
                              >
                                Estándar
                              </button>
                            </div>
                          ) : (
                            <select
                              value={med.frecuenciaHoras || 12}
                              onChange={(e) => handleMedicamentoChange(idx, 'frecuenciaHoras', parseInt(e.target.value))}
                              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                            >
                              {FRECUENCIAS.map(f => (
                                <option key={f.value} value={f.value}>{f.label}</option>
                              ))}
                            </select>
                          )}
                        </div>
                        <div>
                          <label style={{ fontSize: '12px', color: !med.duracionDias ? '#ff9800' : '#666' }}>
                            Duración (días) {!med.duracionDias && <span style={{ color: '#f57c00' }}>⚠️</span>}
                          </label>
                          <input
                            type="number"
                            value={med.duracionDias || ''}
                            onChange={(e) => handleMedicamentoChange(idx, 'duracionDias', parseInt(e.target.value) || null)}
                            placeholder="No especificada"
                            style={{
                              width: '100%',
                              padding: '8px',
                              borderRadius: '4px',
                              border: !med.duracionDias ? '2px solid #ff9800' : '1px solid #ddd',
                              backgroundColor: !med.duracionDias ? '#fff8e1' : '#fff'
                            }}
                          />
                          {!med.duracionDias && (
                            <p style={{ fontSize: '10px', color: '#f57c00', margin: '4px 0 0' }}>
                              No se detectó duración en la receta
                            </p>
                          )}
                        </div>
                      </div>

                      <div style={{ marginTop: '12px' }}>
                        <label style={{ fontSize: '12px', color: '#666' }}>Instrucciones</label>
                        <input
                          type="text"
                          value={med.instrucciones || ''}
                          onChange={(e) => handleMedicamentoChange(idx, 'instrucciones', e.target.value)}
                          placeholder="Dar con comida, evitar sol..."
                          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        <div className="modal-footer" style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '16px 24px',
          borderTop: '1px solid #eee'
        }}>
          <button
            onClick={onRescan}
            style={{
              padding: '10px 20px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              backgroundColor: '#fff',
              cursor: 'pointer',
              color: '#666'
            }}
          >
            🔄 Volver a escanear
          </button>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={onClose}
              style={{
                padding: '10px 20px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                backgroundColor: '#fff',
                cursor: 'pointer'
              }}
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              style={{
                padding: '10px 24px',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: loading ? '#ccc' : '#4CAF50',
                color: '#fff',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontWeight: '500'
              }}
            >
              {loading ? 'Guardando...' : 'Guardar entrada'}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        /* ========================================
           BASE STYLES - Override Bootstrap
           ======================================== */
        .review-data-overlay {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          background-color: rgba(0, 0, 0, 0.5) !important;
          display: flex !important;
          justify-content: center !important;
          align-items: center !important;
          z-index: 1050 !important;
          padding: 20px !important;
        }

        .review-data-modal {
          background: #fff !important;
          border-radius: 12px !important;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
          width: 100% !important;
          max-width: 640px !important;
          max-height: 90vh !important;
          display: flex !important;
          flex-direction: column !important;
          overflow: hidden !important;
          position: relative !important;
        }

        .review-data-modal .modal-header {
          display: flex !important;
          justify-content: space-between !important;
          align-items: center !important;
          padding: 16px 24px !important;
          border-bottom: 1px solid #eee !important;
          flex-shrink: 0 !important;
          background: #fff !important;
        }

        .review-data-modal .modal-header h3 {
          margin: 0 !important;
          font-size: 18px !important;
          font-weight: 600 !important;
          color: #333 !important;
        }

        .review-data-modal .modal-close {
          background: none !important;
          border: none !important;
          font-size: 28px !important;
          cursor: pointer !important;
          color: #666 !important;
          padding: 0 !important;
          line-height: 1 !important;
          width: 36px !important;
          height: 36px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          border-radius: 50% !important;
          transition: background 0.2s !important;
        }

        .review-data-modal .modal-close:hover {
          background: #f5f5f5 !important;
        }

        .review-data-modal .modal-body {
          flex: 1 !important;
          overflow-y: auto !important;
          -webkit-overflow-scrolling: touch !important;
        }

        .review-data-modal .modal-footer {
          flex-shrink: 0 !important;
          background: #fff !important;
        }

        /* ========================================
           MOBILE FULLSCREEN (<768px)
           ======================================== */
        @media (max-width: 767px) {
          .review-data-overlay {
            padding: 0 !important;
            align-items: stretch !important;
          }

          .review-data-modal {
            max-width: 100% !important;
            width: 100% !important;
            height: 100vh !important;
            height: 100dvh !important;
            max-height: 100vh !important;
            max-height: 100dvh !important;
            margin: 0 !important;
            border-radius: 0 !important;
          }

          .review-data-modal .modal-header {
            padding: 16px !important;
          }

          .review-data-modal .modal-header h3 {
            font-size: 17px !important;
          }

          .review-data-modal .modal-close {
            font-size: 32px !important;
            width: 44px !important;
            height: 44px !important;
            margin-right: -8px !important;
          }

          .review-data-modal .modal-body {
            padding: 16px !important;
          }

          .review-data-modal .modal-footer {
            flex-direction: column-reverse !important;
            gap: 12px !important;
            padding: 16px !important;
            border-top: 1px solid #eee !important;
          }

          .review-data-modal .modal-footer > button,
          .review-data-modal .modal-footer > div {
            width: 100% !important;
          }

          .review-data-modal .modal-footer > div {
            display: flex !important;
            flex-direction: column !important;
            gap: 8px !important;
          }

          .review-data-modal .modal-footer button {
            width: 100% !important;
            padding: 14px 20px !important;
            font-size: 16px !important;
          }
        }
      `}</style>
    </div>
  );
}
